import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'underscore';
import classNames from 'classnames';

import AppendToBody from 'sharedreact/components/AppendToBody';
import { translate } from 'sharedreact/utils/LanguageSupport';
import { useGlobalStackState } from 'sharedreact/utils/GlobalState';
import TooltipZIndexContext from 'sharedreact/contexts/TooltipZIndexContext';

type Props = {
  id: string;
  language: string;
  title: string;
  children: React.ReactNode;
  show: boolean;
  zIndex?: number;
  closeText?: string;
  confirmText?: string;
  hideCloseButton?: boolean;
  hideConfirmButton?: boolean;
  disableDismissOnClickOutside?: boolean;
  noHeader?: boolean;
  noFooter?: boolean;
  disableConfirm?: boolean;
  disableClose?: boolean;
  small?: boolean;
  large?: boolean;
  useYesNo?: boolean;

  primaryButton?: 'close' | 'confirm';

  extraFooterComponent?: React.ReactNode;
  extraHeaderComponent?: React.ReactNode;

  className?: string;

  onShown?: () => void;

  onClose: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onClosed?: () => void;
  onConfirm?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
};

type FooterProps = {
  id: string;
  disableConfirm: boolean | undefined;
  disableClose: boolean | undefined;
  closeText: string | undefined;
  confirmText: string | undefined;
  hideCloseButton: boolean | undefined;
  hideConfirmButton: boolean | undefined;
  extraFooterComponent: React.ReactNode | undefined;
  primaryButton: 'close' | 'confirm' | undefined;
  useYesNo: boolean | undefined;
  onClose: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
}

function DialogFooter ({
  id, hideConfirmButton, hideCloseButton, disableConfirm, disableClose,
  confirmText, closeText, extraFooterComponent,
  onClose, onConfirm, primaryButton, useYesNo
} : FooterProps) {
  const closeButtonText = (closeText != null ? closeText : (useYesNo ? translate('shared.general.no') : translate('shared.general.close')));
  const confirmButtonText = (confirmText  != null ? confirmText : (useYesNo ? translate('shared.general.yes') : translate('shared.general.ok')));

  return (
    <div className='esko_modal-footer'>
      {extraFooterComponent}
      {!hideCloseButton
      ? (
        <button id={id + '_close'} type='button' className={classNames('esko_btn', primaryButton === 'close' ? 'esko_btn-primary' : 'esko_btn-default')} onClick={onClose} disabled={disableClose}>
          {closeButtonText}
        </button>
      )
      : undefined
      }
      {!hideConfirmButton && onConfirm != null
      ? (
        <button id={id + '_confirm'} type='button' className={classNames('esko_btn', primaryButton === 'confirm' ? 'esko_btn-primary' : 'esko_btn-default')} onClick={onConfirm} disabled={disableConfirm}>
          {confirmButtonText}
        </button>
      )
      : undefined
      }
    </div>
  );
}

type ParentProps = {
  id: string;
  zIndex: number;
  disableConfirm?: boolean;
  children?: React.ReactNode;
  isVisible: boolean;
  onClose: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
};

function DialogParent ({
  id,
  zIndex,
  disableConfirm,
  children,
  isVisible,
  onClose,
  onConfirm
} : ParentProps) {
  const [topDialogId, pushTopDialogId, popTopDialogId] = useGlobalStackState<string>('topDialogId');

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (topDialogId === id) {
      function handleKeyDown (event: KeyboardEvent) {
        if (event.key === 'Escape') {
          onCloseRef.current();
        }
        else if (event.key === 'Enter') {
          if (!disableConfirm && onConfirmRef.current != null) {
            onConfirmRef.current();
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [id, topDialogId]);

  useEffect(() => {
    pushTopDialogId(id);
    
    return () => { popTopDialogId(id); };
  }, []);

  return (
    <TooltipZIndexContext.Provider value={zIndex + 10}>
      <div className={classNames('esko_modalparent', {'esko_modal-open': isVisible})} style={{zIndex: zIndex}}>
        {children}
      </div>
    </TooltipZIndexContext.Provider>
  );
}

function Dialog ({
  id, show, title, disableDismissOnClickOutside, zIndex = 2500,
  className, onClose, onClosed, onShown, noHeader = false,
  noFooter = false, disableConfirm, disableClose, children,
  hideCloseButton = false, hideConfirmButton = false, onConfirm,
  primaryButton, useYesNo, confirmText, closeText, extraFooterComponent,
  extraHeaderComponent, small, large
} : Props) {
  const [addToPage, setAddToPage] = useState(false);
  const [didShow, setDidShow] = useState(false);
  
  useEffect(() => {
    if(addToPage) {
      setTimeout(() => { 
        setDidShow(true); 
        if (onShown != null) {
          onShown();
        }
      }, 100);
    }
  }, [addToPage]);

  const modalDialogRef = useRef<HTMLDivElement | null>(null);

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const isInitial = useRef(true);
  useEffect(() => {
    if (show) {
      setAddToPage(true);
    } else {
      if (!isInitial.current) {  
        setDidShow(false);
    
        const handleTransitionEnded = () => {
          modalDialogRef.current?.removeEventListener('transitionend', handleTransitionEnded);
          setAddToPage(false);
          if ( onClosedRef.current != null) {
            onClosedRef.current();
          }
        };

        modalDialogRef.current?.addEventListener('transitionend', handleTransitionEnded);
      }
    }

    isInitial.current = false;
  }, [show]);

  const modalRef = useRef<HTMLDivElement | null>(null);

  const dissmissFromBackdrop = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef != null && modalRef.current === event.target) {
      onClose();
    }
  }, [onClose]);

  const smallModal = small && !large;
  const largeModal = large && !small;
  const dialogClassNames = useMemo(() => classNames(
    'esko_modal-dialog',
    {'esko_modal-sm': smallModal},
    {'esko_modal-lg': largeModal},
    className
  ), [smallModal, largeModal]);

  return (
    addToPage
    ? (
      <AppendToBody>
        <DialogParent zIndex={zIndex} id={id} onClose={onClose} onConfirm={onConfirm}
          disableConfirm={disableConfirm} isVisible={didShow}
        >
          <div
            className={classNames('esko_modal-backdrop', {'esko_show': didShow})}
            role='dialog'
            tabIndex={-1} />
          <div
            className={classNames('esko_modal', {'esko_show': didShow})}
            onClick={disableDismissOnClickOutside ? undefined : dissmissFromBackdrop}
            ref={modalRef}
          >
            <div id={id} className={dialogClassNames} role='document'
              ref={modalDialogRef}>
              <div className='esko_modal-content'>
                {!noHeader
                ? <div className='esko_modal-header'>
                  <button id={id + '_close_x'} type='button' className='esko_close' aria-label='Close' onClick={onClose}>
                    <span aria-hidden='true'>&times;</span>
                  </button>
                  <h4 className='esko_modal-title'>{title}</h4>
                  {extraHeaderComponent}
                </div>
                : null
                }
                <div className='esko_modal-body'>
                  {children}
                </div>
                {!noFooter
                ? <DialogFooter id={id} hideConfirmButton={hideConfirmButton} hideCloseButton={hideCloseButton}
                    closeText={closeText} confirmText={confirmText} disableClose={disableClose}
                    disableConfirm={disableConfirm} extraFooterComponent={extraFooterComponent}
                    primaryButton={primaryButton} useYesNo={useYesNo}
                    onClose={onClose} onConfirm={onConfirm} />
                : null
                }
              </div>
            </div>
          </div>
        </DialogParent>
      </AppendToBody>
    )
    : null
  );
}

export default Dialog;
